import { useQuery } from '@apollo/client';
import { Link, useParams } from 'react-router-dom';
import { renderPostBlock } from '../../../components/Post/PostBlocks/PostBlocks';
import {
  BookmarkIcon,
  HeartIcon,
} from '../../../components/UserFeed/FeedIcons';
import { GET_REPLY_BY_ID_QUERY } from '../../../graphql/resolvers/postQueries/getReplyById.gql';
import { replyAnalysisPath } from '../../../helpers/replyAnalysisPath';
import { formatScore } from '../../../helpers/scoreDisplay';
import { useFeedPostSubscriptions } from '../../../hooks/useFeedPostSubscriptions';
import { usePostInteractions } from '../../../hooks/usePostInteractions';
import type { FeedPost } from '../../../interfaces/feed.interface';
import type { TextBlock } from '../../../interfaces/textEditor.interface';
import './reply.scss';

/**
 * TRIL-349 / TRIL-351: reply detail page at `/reply/:replyId`. Mirrors the post
 * detail page — full body, quality score, a prominent link to the parent post,
 * and a working like/bookmark row. A reply is returned as the `Post` type, so it
 * normalizes into `Post:{replyId}` in the Apollo cache and reuses the same
 * `usePostInteractions` hook the post page uses; the server mutations are
 * reply-aware (TRIL-351), so the toggles persist against the reply's own rows.
 */
export default function UserReply() {
  const { replyId } = useParams<{ replyId: string }>();

  const { data, loading, error } = useQuery(GET_REPLY_BY_ID_QUERY, {
    variables: { replyId: replyId ?? '' },
    skip: !replyId,
    fetchPolicy: 'cache-and-network',
  });
  // Hooks must run before any early return.
  const { toggleLike, toggleBookmark } = usePostInteractions();
  // TRIL-351: register this reader as a watcher of the reply id (PostWatchersIndex)
  // so like/bookmark INTERACTION_UPDATED WS broadcasts land here and update the
  // Post:{replyId} counts live — same path the post detail page uses for posts.
  useFeedPostSubscriptions(replyId ? [replyId] : []);

  const reply = data?.getReplyById;

  if (loading && !reply) {
    return <div className="REPLY_DETAIL__state">Loading reply…</div>;
  }
  if (error) {
    return <div className="REPLY_DETAIL__state">Couldn’t load this reply.</div>;
  }
  if (!reply) {
    return (
      <div className="REPLY_DETAIL__state">
        This reply doesn’t exist or is no longer available.
      </div>
    );
  }

  const author =
    reply.postAnalytics?.author?.username || reply.username || 'unknown';
  const score = typeof reply.score === 'number' ? reply.score : null;
  const vi = reply.viewerInteractions;
  const liveHasLiked = !!vi?.hasLiked;
  const liveHasBookmarked = !!vi?.hasBookmarked;
  const blocks = (reply.textBlocks ?? []).filter(Boolean) as TextBlock[];
  const created = reply.createdAt
    ? new Date(reply.createdAt as string).toLocaleDateString()
    : null;

  // The reply is cached as Post:{replyId}; usePostInteractions patches that entry
  // and the reply-aware mutations persist it. postId drives the toggle target.
  const likeTarget = { ...reply, postId: reply.id } as unknown as FeedPost;

  return (
    <div className="POST REPLY_DETAIL">
      {reply.parentPostId && (
        <Link
          to={`/post/${reply.parentPostId}`}
          className="REPLY_DETAIL__parent-link"
        >
          ← View the story this replies to
        </Link>
      )}

      <div className="POST__ACTIONS__CONTAINER">
        <div className="POST__ACTIONS_COL">
          <Link
            to={replyAnalysisPath(reply.id ?? '')}
            className="post-score"
            title="View full reply analysis"
          >
            SQS: {formatScore(score)}
          </Link>
          <button
            type="button"
            className={`post-action ${liveHasLiked ? 'post-action--active-like' : ''}`}
            onClick={() => toggleLike(likeTarget)}
            aria-label="Like"
          >
            <HeartIcon
              size={18}
              fill={liveHasLiked ? '#e0245e' : 'currentColor'}
            />
            <span>{vi?.likeCount ?? 0}</span>
          </button>
          <button
            type="button"
            className={`post-action ${liveHasBookmarked ? 'post-action--active-bookmark' : ''}`}
            onClick={() => toggleBookmark(likeTarget)}
            aria-label="Bookmark"
          >
            <BookmarkIcon size={18} />
            <span>{vi?.bookmarkCount ?? 0}</span>
          </button>
          {/* No conversation icon: replies-to-a-reply aren't supported yet, so
              the comment count is intentionally omitted from the DOM. */}
        </div>

        <article className="REPLY_DETAIL__body">
          <header className="REPLY_DETAIL__meta">
            <span className="REPLY_DETAIL__author">@{author}</span>
            {created && (
              <span className="REPLY_DETAIL__date"> · {created}</span>
            )}
          </header>
          {blocks.map((block, i) => (
            <div key={block.id ?? `block-${i}`}>{renderPostBlock(block)}</div>
          ))}
        </article>
      </div>
    </div>
  );
}
